import React, { useState, useEffect, useCallback } from 'react';
import { PorteroLayout } from '../../components/portero/PorteroLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import {
  adminGetUsuarios, adminCrearUsuario, adminEditarUsuario,
  adminGetDispositivosUsuario, adminGetConfig, adminSetConfig,
} from '../../lib/porteroApi';
import { toast } from 'sonner';
import { Users, Cpu, Plus, Save, Trash2, Loader2, Zap, Power, DoorOpen, Bell, ShieldCheck } from 'lucide-react';

const ICONOS = [
  { value: 'zap', label: 'Rayo', Icon: Zap },
  { value: 'power', label: 'Power', Icon: Power },
  { value: 'door', label: 'Puerta', Icon: DoorOpen },
  { value: 'bell', label: 'Timbre', Icon: Bell },
  { value: 'shield', label: 'Escudo', Icon: ShieldCheck },
];

const COLORES = [
  { value: '#10b981', label: 'Verde' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#ef4444', label: 'Rojo' },
  { value: '#f59e0b', label: 'Ámbar' },
  { value: '#8b5cf6', label: 'Violeta' },
  { value: '#ec4899', label: 'Rosa' },
  { value: '#06b6d4', label: 'Cyan' },
];

const emptyBtn = () => ({
  nombre: '', comando: '', comando_off: '', tipo: 'pulso', icono: 'zap', color: '#10b981',
});

export default function PorteroConfigPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devLoading, setDevLoading] = useState(false);
  const [configs, setConfigs] = useState([]);

  // Edit state
  const [editDevice, setEditDevice] = useState(null);
  const [editBotones, setEditBotones] = useState([]);
  const [editLayout, setEditLayout] = useState('grid');
  const [saving, setSaving] = useState(false);

  // New user dialog
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: '', nombre: '', rol: 'usuario' });

  const loadUsuarios = useCallback(async () => {
    try {
      const d = await adminGetUsuarios();
      setUsuarios(d.usuarios || []);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsuarios(); }, [loadUsuarios]);

  const selectUser = async (user) => {
    setSelectedUser(user);
    setEditDevice(null);
    setDevLoading(true);
    try {
      const [devRes, confRes] = await Promise.all([
        adminGetDispositivosUsuario(user.id),
        adminGetConfig(user.id),
      ]);
      setDevices(devRes.dispositivos || []);
      setConfigs(confRes.configs || []);
    } catch (e) { toast.error(e.message); }
    setDevLoading(false);
  };

  const openDeviceConfig = (dev) => {
    setEditDevice(dev);
    const existing = configs.find(c => c.device_id === dev.id);
    setEditBotones(existing?.botones?.length > 0 ? [...existing.botones] : [emptyBtn()]);
    setEditLayout(existing?.layout || 'grid');
  };

  const addBoton = () => setEditBotones([...editBotones, emptyBtn()]);

  const removeBoton = (i) => setEditBotones(editBotones.filter((_, idx) => idx !== i));

  const updateBoton = (i, field, val) => {
    const updated = [...editBotones];
    updated[i] = { ...updated[i], [field]: val };
    setEditBotones(updated);
  };

  const handleSave = async () => {
    if (!editDevice || !selectedUser) return;
    const valid = editBotones.filter(b => b.nombre && b.comando);
    if (valid.length === 0) {
      toast.error('Agrega al menos un botón con nombre y comando');
      return;
    }
    setSaving(true);
    try {
      await adminSetConfig(selectedUser.id, editDevice.id, {
        botones: valid,
        device_name: editDevice.name,
        layout: editLayout,
      });
      toast.success(`${valid.length} botones guardados para ${editDevice.name}`);
      // Refresh configs
      const confRes = await adminGetConfig(selectedUser.id);
      setConfigs(confRes.configs || []);
      setEditDevice(null);
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleCreateUser = async () => {
    if (!newUserForm.email) { toast.error('Email requerido'); return; }
    try {
      await adminCrearUsuario(newUserForm);
      toast.success('Usuario creado');
      setShowNewUser(false);
      setNewUserForm({ email: '', nombre: '', rol: 'usuario' });
      loadUsuarios();
    } catch (e) { toast.error(e.message); }
  };

  const toggleUserRole = async (u) => {
    const newRol = u.rol === 'admin' ? 'usuario' : 'admin';
    try {
      await adminEditarUsuario(u.id, { rol: newRol });
      toast.success(`${u.nombre}: ahora es ${newRol}`);
      loadUsuarios();
    } catch (e) { toast.error(e.message); }
  };

  const getDeviceConfigCount = (devId) => {
    const c = configs.find(c => c.device_id === devId);
    return c?.botones?.length || 0;
  };

  return (
    <PorteroLayout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="portero-config">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Configuraci&oacute;n</h1>
            <p className="text-xs text-zinc-500">Administra usuarios y botones de control</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Users list */}
          <Card className="bg-zinc-900 border-zinc-800 lg:col-span-1">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                  <Users className="h-4 w-4" /> Usuarios
                </CardTitle>
                <Button size="sm" variant="ghost" className="text-emerald-400 h-7" onClick={() => setShowNewUser(true)} data-testid="btn-nuevo-portero-user">
                  <Plus className="h-3 w-3 mr-1" /> Nuevo
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              {loading ? <Skeleton className="h-20 bg-zinc-800" /> : usuarios.map(u => (
                <div
                  key={u.id}
                  onClick={() => selectUser(u)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${selectedUser?.id === u.id ? 'bg-zinc-800 border border-emerald-500/30' : 'hover:bg-zinc-800/50'}`}
                  data-testid={`user-item-${u.email}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-white">{u.nombre}</div>
                      <div className="text-xs text-zinc-500">{u.email}</div>
                    </div>
                    <Badge
                      variant="outline"
                      className={`text-[10px] cursor-pointer ${u.rol === 'admin' ? 'border-emerald-500/50 text-emerald-400' : 'border-zinc-700 text-zinc-500'}`}
                      onClick={(e) => { e.stopPropagation(); toggleUserRole(u); }}
                    >
                      {u.rol}
                    </Badge>
                  </div>
                </div>
              ))}
              {!loading && usuarios.length === 0 && (
                <p className="text-xs text-zinc-600 text-center py-4">Sin usuarios</p>
              )}
            </CardContent>
          </Card>

          {/* Devices & Button Config */}
          <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                {selectedUser ? `Dispositivos de ${selectedUser.nombre}` : 'Selecciona un usuario'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedUser ? (
                <p className="text-sm text-zinc-600 text-center py-8">Selecciona un usuario de la lista</p>
              ) : devLoading ? (
                <Skeleton className="h-32 bg-zinc-800" />
              ) : devices.length === 0 ? (
                <p className="text-sm text-zinc-600 text-center py-8">
                  Este usuario no tiene dispositivos en 4sat.cl o a&uacute;n no ha hecho login.
                </p>
              ) : editDevice ? (
                /* Button editor */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-white">{editDevice.name}</h3>
                      <p className="text-xs text-zinc-500">IMEI: {editDevice.imei} | Patente: {editDevice.plate}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => setEditDevice(null)}>Volver</Button>
                  </div>

                  <div className="flex items-center gap-3">
                    <Label className="text-zinc-400 text-xs">Layout:</Label>
                    <Select value={editLayout} onValueChange={setEditLayout}>
                      <SelectTrigger className="w-32 h-8 bg-zinc-800 border-zinc-700 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="grid">Grid</SelectItem>
                        <SelectItem value="list">Lista</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {editBotones.map((btn, i) => (
                    <div key={i} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-400 font-bold">Bot&oacute;n {i + 1}</span>
                        {editBotones.length > 1 && (
                          <Button size="sm" variant="ghost" className="h-6 text-red-400" onClick={() => removeBoton(i)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-zinc-500 text-[10px]">Nombre</Label>
                          <Input value={btn.nombre} onChange={e => updateBoton(i, 'nombre', e.target.value)} placeholder="Abrir Port&oacute;n" className="h-8 bg-zinc-800 border-zinc-700 text-white text-xs" />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-[10px]">Tipo</Label>
                          <Select value={btn.tipo} onValueChange={v => updateBoton(i, 'tipo', v)}>
                            <SelectTrigger className="h-8 bg-zinc-800 border-zinc-700 text-white text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pulso">Pulso (timbre)</SelectItem>
                              <SelectItem value="toggle">Toggle (ON/OFF)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-500 text-[10px]">Comando GPRS</Label>
                        <Input value={btn.comando} onChange={e => updateBoton(i, 'comando', e.target.value)} placeholder="setdigout ?1 ? 1" className="h-8 bg-zinc-800 border-zinc-700 text-white text-xs font-mono" />
                      </div>
                      {btn.tipo === 'toggle' && (
                        <div>
                          <Label className="text-zinc-500 text-[10px]">Comando OFF (para desactivar)</Label>
                          <Input value={btn.comando_off || ''} onChange={e => updateBoton(i, 'comando_off', e.target.value)} placeholder="setdigout ?0 ? 0" className="h-8 bg-zinc-800 border-zinc-700 text-white text-xs font-mono" />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-zinc-500 text-[10px]">Icono</Label>
                          <Select value={btn.icono} onValueChange={v => updateBoton(i, 'icono', v)}>
                            <SelectTrigger className="h-8 bg-zinc-800 border-zinc-700 text-white text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ICONOS.map(ic => (
                                <SelectItem key={ic.value} value={ic.value}>
                                  <span className="flex items-center gap-2"><ic.Icon className="h-3 w-3" />{ic.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-[10px]">Color</Label>
                          <Select value={btn.color} onValueChange={v => updateBoton(i, 'color', v)}>
                            <SelectTrigger className="h-8 bg-zinc-800 border-zinc-700 text-white text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {COLORES.map(c => (
                                <SelectItem key={c.value} value={c.value}>
                                  <span className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.value }} />
                                    {c.label}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {/* Preview */}
                      <div className="flex items-center gap-3 pt-2 border-t border-zinc-700/30">
                        <span className="text-[10px] text-zinc-600">Preview:</span>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: btn.color || '#10b981' }}>
                          {(() => { const Ic = ICONOS.find(x => x.value === btn.icono)?.Icon || Zap; return <Ic className="h-5 w-5 text-white" />; })()}
                        </div>
                        <span className="text-xs text-zinc-400">{btn.nombre || 'Sin nombre'}</span>
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-3">
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={addBoton}>
                      <Plus className="h-3 w-3 mr-1" /> Agregar bot&oacute;n
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={handleSave} disabled={saving} data-testid="btn-guardar-config-portero">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3 w-3 mr-1" /> Guardar</>}
                    </Button>
                  </div>
                </div>
              ) : (
                /* Device list */
                <div className="space-y-2">
                  {devices.map(dev => {
                    const btnCount = getDeviceConfigCount(dev.id);
                    return (
                      <div
                        key={dev.id}
                        onClick={() => openDeviceConfig(dev)}
                        className="p-3 rounded-lg border border-zinc-800 hover:border-zinc-700 cursor-pointer transition-colors flex items-center justify-between"
                        data-testid={`device-config-${dev.id}`}
                      >
                        <div>
                          <div className="text-sm font-medium text-white">{dev.name}</div>
                          <div className="text-xs text-zinc-500">{dev.imei} | {dev.plate}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={dev.online === 'online' ? 'default' : 'secondary'} className="text-[10px]">
                            {dev.online}
                          </Badge>
                          {btnCount > 0 && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">
                              {btnCount} botones
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* New User Dialog */}
        <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Nuevo Usuario Portero</DialogTitle>
              <DialogDescription className="text-zinc-500">
                El usuario deber&aacute; hacer login con sus credenciales 4sat.cl para vincular sus dispositivos.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-400">Email (mismo que en 4sat.cl)</Label>
                <Input value={newUserForm.email} onChange={e => setNewUserForm({...newUserForm, email: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" data-testid="new-portero-user-email" />
              </div>
              <div>
                <Label className="text-zinc-400">Nombre</Label>
                <Input value={newUserForm.nombre} onChange={e => setNewUserForm({...newUserForm, nombre: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" />
              </div>
              <div>
                <Label className="text-zinc-400">Rol</Label>
                <Select value={newUserForm.rol} onValueChange={v => setNewUserForm({...newUserForm, rol: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usuario">Usuario</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setShowNewUser(false)}>Cancelar</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleCreateUser} data-testid="btn-crear-portero-user">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PorteroLayout>
  );
}
